import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faMicrophone, faTimes, faMinus, faSave, faSignInAlt } from '@fortawesome/free-solid-svg-icons';
import { MLSFieldMapping, AIQueryHelpers, QueryPatterns } from './mlsFieldMapping';
import axiosInstance from '../../helpers/api';
import { mapEventBus } from '../../utils/mapEventBus';
import './AgentK.css';

interface AgentKProps {
  validProperties: any[];
  onSearchCriteria: (criteria: any) => void;
  isSearching?: boolean;
  agentPhoto?: string;
  agentName?: string;
  externalCriteria?: any; // Criteria from external sources like header search
}

const AgentK: React.FC<AgentKProps> = ({ 
  validProperties, 
  onSearchCriteria,
  isSearching = false,
  agentPhoto,
  agentName = "Agent Kairos",
  externalCriteria = {}
}) => {
  const [query, setQuery] = useState('');
  const [isMinimized, setIsMinimized] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [aiResponse, setAiResponse] = useState<string>('');
  // Initialize with external criteria if available
  const [currentCriteria, setCurrentCriteria] = useState<any>(externalCriteria || {});
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [proactivePrompt, setProactivePrompt] = useState<any>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [saving, setSaving] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [signInData, setSignInData] = useState({ email: '', password: '' });
  const [signInLoading, setSignInLoading] = useState(false);
  const [signInError, setSignInError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Check if user is authenticated (either main portal or customer portal)
  const [isAuthenticated, setIsAuthenticated] = useState(
    !!localStorage.getItem('customerPortalToken') || !!localStorage.getItem('auth_token')
  );
  
  // Initialize with external criteria on mount
  useEffect(() => {
    console.log('🎯 Agent Kairos initializing with criteria:', externalCriteria);
    if (Object.keys(externalCriteria).length > 0) {
      // Generate initial AI response
      setAiResponse(generateResponse(externalCriteria));
    }
  }, []); // Run once on mount
  
  // Sync external criteria with current criteria
  useEffect(() => {
    console.log('🎯 Agent Kairos received external criteria:', externalCriteria);
    if (Object.keys(externalCriteria).length > 0) {
      // Merge external criteria with current criteria
      const merged = { ...currentCriteria, ...externalCriteria };
      console.log('🎯 Agent Kairos merged criteria:', merged);
      setCurrentCriteria(merged);
      
      // Generate AI response for external search
      if (externalCriteria.city || externalCriteria.location || externalCriteria.bedrooms || externalCriteria.beds || externalCriteria.maxPrice || externalCriteria.priceMax) {
        setAiResponse(generateResponse(merged));
      }
    }
  }, [externalCriteria]);

  // Calculate property statistics
  const propertyStats = validProperties.length > 0 ? {
    count: validProperties.length,
    avgPrice: validProperties.reduce((sum, p) => sum + (p.price || 0), 0) / validProperties.length,
    avgBeds: Math.round(validProperties.reduce((sum, p) => sum + (p.bedrooms || 0), 0) / validProperties.length * 10) / 10,
    avgSqft: Math.round(validProperties.reduce((sum, p) => sum + (p.sqft || 0), 0) / validProperties.length),
    newListings: validProperties.filter(p => p.daysOnMarket && p.daysOnMarket <= 7).length,
    hotProperties: validProperties.filter(p => p.daysOnMarket && p.daysOnMarket < 3).length
  } : null;

  // Enhanced natural language parser using MLS field mapping
  const parseNaturalQuery = (text: string): any => {
    const criteria: any = {};
    const lowerText = text.toLowerCase();

    // Extract city using MLS mapping
    MLSFieldMapping.location.cities.forEach(city => {
      if (lowerText.includes(city.toLowerCase())) {
        criteria.city = city;
      }
    });

    // Check for area references (e.g., "east valley")
    Object.entries(MLSFieldMapping.location.areas).forEach(([area, cities]) => {
      if (lowerText.includes(area)) {
        criteria.cities = cities;
      }
    });

    // Extract bedrooms
    const bedPatterns = [
      /(\d+)\s*(?:bed|bedroom|br|beds)/i,
      /(\d+)\s*bd/i
    ];
    for (const pattern of bedPatterns) {
      const match = lowerText.match(pattern);
      if (match) {
        criteria.bedrooms = parseInt(match[1]);
        break;
      }
    }

    // Extract bathrooms
    const bathPatterns = [
      /(\d+(?:\.\d)?)\s*(?:bath|bathroom|ba|baths)/i,
      /(\d+(?:\.\d)?)\s*ba/i
    ];
    for (const pattern of bathPatterns) {
      const match = lowerText.match(pattern);
      if (match) {
        criteria.bathrooms = parseFloat(match[1]);
        break;
      }
    }

    // Extract price
    const simpleNumericMatch = text.match(/(\d{3,}(?:,\d{3})*)/);
    if (simpleNumericMatch) {
      const numericValue = parseInt(simpleNumericMatch[1].replace(/,/g, ''));
      const beforeNumber = text.substring(0, text.indexOf(simpleNumericMatch[0])).toLowerCase();
      const afterNumber = text.substring(text.indexOf(simpleNumericMatch[0]) + simpleNumericMatch[0].length).toLowerCase();
      
      if (beforeNumber.includes('under') || beforeNumber.includes('max') || 
          afterNumber.includes('max') || afterNumber.includes('or less')) {
        criteria.maxPrice = numericValue;
      } else if (beforeNumber.includes('over') || beforeNumber.includes('min') || 
                 afterNumber.includes('min') || afterNumber.includes('or more')) {
        criteria.minPrice = numericValue;
      } else {
        criteria.maxPrice = numericValue;
      }
    }

    // Extract property type
    Object.entries(MLSFieldMapping.propertyType.mappings).forEach(([term, mlsTypes]) => {
      if (lowerText.includes(term)) {
        criteria.propertyType = mlsTypes[0];
      }
    });

    // Extract features
    Object.entries(MLSFieldMapping.features).forEach(([feature, config]) => {
      config.aliases.forEach((alias: string) => {
        if (lowerText.includes(alias)) {
          criteria[feature] = true;
        }
      });
    });

    return criteria;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;

    setSearchHistory([...searchHistory, query]);
    const newCriteria = parseNaturalQuery(query);
    
    // Merge with existing criteria (cumulative search)
    const mergedCriteria = { ...currentCriteria };
    
    Object.entries(newCriteria).forEach(([key, value]) => {
      if (key === 'city' || key === 'propertyType') {
        mergedCriteria[key] = value;
      } else if (key === 'minPrice' || key === 'maxPrice' || key === 'bedrooms' || key === 'bathrooms') {
        mergedCriteria[key] = value;
      } else if (typeof value === 'boolean') {
        mergedCriteria[key] = value;
      } else {
        mergedCriteria[key] = value;
      }
    });
    
    setCurrentCriteria(mergedCriteria);
    onSearchCriteria(mergedCriteria);
    
    // Generate response
    setAiResponse(generateResponse(mergedCriteria));
    
    setTimeout(() => setQuery(''), 500);
  };
  
  const generateResponse = (criteria: any): string => {
    let responseText = "I'm searching for ";
    const parts = [];
    
    if (criteria.bedrooms || criteria.beds) parts.push(`${criteria.bedrooms || criteria.beds}+ bedroom`);
    if (criteria.bathrooms || criteria.baths) parts.push(`${criteria.bathrooms || criteria.baths}+ bathroom`);
    if (criteria.propertyType) parts.push(criteria.propertyType.replace('_', ' '));
    
    if (parts.length > 0) responseText += parts.join(', ') + ' properties ';
    else responseText += 'properties ';
    
    if (criteria.city || criteria.location) responseText += `in ${criteria.city || criteria.location} `;
    else if (criteria.cities) responseText += `in the ${criteria.cities.join(', ')} area `;
    
    const maxPrice = criteria.maxPrice || criteria.priceMax;
    const minPrice = criteria.minPrice || criteria.priceMin;
    
    if (maxPrice && minPrice) {
      responseText += `between $${minPrice.toLocaleString()} and $${maxPrice.toLocaleString()} `;
    } else if (maxPrice) {
      responseText += `under $${maxPrice.toLocaleString()} `;
    } else if (minPrice) {
      responseText += `over $${minPrice.toLocaleString()} `;
    }
    
    responseText += '...';
    
    return responseText;
  };

  const handleVoiceSearch = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
      const recognition = new SpeechRecognition();
      
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'en-US';
      
      recognition.onstart = () => {
        setIsListening(true);
      };
      
      recognition.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        setQuery(transcript);
        setIsListening(false);
        setTimeout(() => {
          inputRef.current?.form?.requestSubmit();
        }, 500);
      };
      
      recognition.onerror = () => {
        setIsListening(false);
      };
      
      recognition.start();
    }
  };

  // Listen for property tour prompts
  useEffect(() => {
    const handleTourPrompt = (prompt: any) => {
      setProactivePrompt(prompt);
      setIsMinimized(false);
    };

    mapEventBus.on('property_tour_prompt', handleTourPrompt);

    return () => {
      mapEventBus.off('property_tour_prompt', handleTourPrompt);
    };
  }, []);

  const handleSaveSearch = async () => {
    if (!searchName.trim()) return;
    
    setSaving(true);
    try {
      // Check for either customer portal token or main auth token
      const authToken = localStorage.getItem('customerPortalToken') || localStorage.getItem('auth_token');
      if (!authToken) {
        setAiResponse('Please sign in to save your search.');
        return;
      }
      
      const response = await axiosInstance.post(
        '/mls-portal/saved-searches',
        {
          search_name: searchName,
          search_criteria: currentCriteria,
          alert_frequency: 'daily'
        },
        {
          headers: { Authorization: `Bearer ${authToken}` }
        }
      );
      
      if (response.data.success) {
        setShowSaveModal(false);
        setSearchName('');
        setAiResponse('✅ Search saved! You\'ll get alerts when new properties match.');
      }
    } catch (error) {
      console.error('Error saving search:', error);
      setAiResponse('Failed to save search. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setSignInError('');
    setSignInLoading(true);
    
    try {
      const response = await axiosInstance.post('/customer-portal/auth/login', {
        email: signInData.email,
        password: signInData.password
      });
      
      if (response.data.success) {
        // Store the token
        localStorage.setItem('customerPortalToken', response.data.token);
        setIsAuthenticated(true);
        setShowSignInModal(false);
        setSignInData({ email: '', password: '' });
        
        // Show success message and open save modal
        setAiResponse('✅ Signed in successfully! Now you can save your search.');
        if (Object.keys(currentCriteria).length > 0) {
          setSearchName(`${currentCriteria.city || 'Properties'} - ${Object.keys(currentCriteria).length} filters`);
          setShowSaveModal(true);
        }
      }
    } catch (error: any) {
      console.error('Sign in error:', error);
      if (error.response?.status === 401) {
        setSignInError('Invalid email or password. Please check your credentials.');
      } else if (error.response?.status === 404) {
        setSignInError('Login service not found. Please contact support.');
      } else if (error.response?.status === 500) {
        setSignInError('Server error. Please try again later.');
      } else {
        setSignInError('Failed to sign in. Please check your connection and try again.');
      }
    } finally {
      setSignInLoading(false);
    }
  };

  return (
    <div className={`agent-k-panel ${isMinimized ? 'minimized' : ''}`}>
      {/* Header */}
      <div className="agent-k-header" onClick={() => isMinimized && setIsMinimized(false)}>
        <div className="agent-k-avatar">
          {agentPhoto ? (
            <img 
              src={agentPhoto} 
              alt={agentName} 
              style={{ width: '100%', height: '100%', borderRadius: '50%' }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
                (e.target as HTMLImageElement).nextElementSibling?.classList.remove('d-none');
              }}
            />
          ) : (
            <span>K</span>
          )}
        </div>
        {!isMinimized && (
          <>
            <div className="agent-k-info">
              <h3 className="agent-k-name">{agentName}</h3>
              <p className="agent-k-subtitle">Your AI Search Assistant</p>
            </div>
            <button 
              className="agent-k-minimize"
              onClick={(e) => {
                e.stopPropagation();
                setIsMinimized(true);
              }}
            >
              <FontAwesomeIcon icon={faMinus} />
            </button>
          </>
        )}
      </div>

      {/* Content */}
      {!isMinimized && (
        <div className="agent-k-content">
          {/* Proactive Prompt */}
          {proactivePrompt && (
            <div className="agent-k-prompt">
              <p className="agent-k-prompt-message">{proactivePrompt.message}</p>
              <div className="agent-k-prompt-actions">
                {proactivePrompt.actions?.map((action: any, idx: number) => (
                  <button
                    key={idx}
                    className="agent-k-prompt-btn"
                    onClick={() => {
                      if (action.action === 'send_message') {
                        setQuery(action.message || action.label);
                        setProactivePrompt(null);
                      } else if (action.action === 'dismiss') {
                        setProactivePrompt(null);
                      }
                    }}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Map Summary */}
          {propertyStats && (
            <div className="agent-k-summary">
              <h4 className="agent-k-summary-title">Map Overview</h4>
              <div className="agent-k-stats">
                <div className="agent-k-stat">
                  <span className="agent-k-stat-icon">🏠</span>
                  <div>
                    <p className="agent-k-stat-value">{propertyStats.count}</p>
                    <p className="agent-k-stat-label">Properties</p>
                  </div>
                </div>
                <div className="agent-k-stat">
                  <span className="agent-k-stat-icon">💰</span>
                  <div>
                    <p className="agent-k-stat-value">${Math.round(propertyStats.avgPrice / 1000)}K</p>
                    <p className="agent-k-stat-label">Avg Price</p>
                  </div>
                </div>
                <div className="agent-k-stat">
                  <span className="agent-k-stat-icon">🛏️</span>
                  <div>
                    <p className="agent-k-stat-value">{propertyStats.avgBeds}</p>
                    <p className="agent-k-stat-label">Avg Beds</p>
                  </div>
                </div>
                <div className="agent-k-stat">
                  <span className="agent-k-stat-icon">📐</span>
                  <div>
                    <p className="agent-k-stat-value">{propertyStats.avgSqft.toLocaleString()}</p>
                    <p className="agent-k-stat-label">Avg Sq Ft</p>
                  </div>
                </div>
              </div>
              {propertyStats.hotProperties > 0 && (
                <div style={{ marginTop: '12px', fontSize: '13px', color: '#ef4444' }}>
                  ⚡ {propertyStats.hotProperties} hot properties just listed!
                </div>
              )}
            </div>
          )}

          {/* AI Message */}
          <div className="agent-k-message">
            <div className="agent-k-message-bubble">
              {aiResponse || (validProperties.length > 0 ? (
                <>I found <strong>{validProperties.length} properties</strong> in this area. What specific features are you looking for?</>
              ) : (
                `Hi! I'm ${agentName}. Tell me what kind of home you're looking for and I'll help you find it.`
              ))}
            </div>
          </div>

          {/* Search Section */}
          <div className="agent-k-search">
            <form onSubmit={handleSubmit} className="agent-k-search-form">
              <input
                ref={inputRef}
                type="text"
                placeholder="Tell me what you're looking for..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className={`agent-k-search-input ${isListening ? 'listening' : ''}`}
                disabled={isListening}
              />
              <div className="agent-k-search-buttons">
                <button 
                  type="button"
                  className="agent-k-btn"
                  onClick={handleVoiceSearch}
                  disabled={isListening}
                >
                  <FontAwesomeIcon icon={faMicrophone} />
                  Voice
                </button>
                <button 
                  type="submit" 
                  className="agent-k-btn primary"
                  disabled={!query.trim() || isSearching}
                >
                  <FontAwesomeIcon icon={faPaperPlane} />
                  Search
                </button>
              </div>
            </form>

            {/* Quick Filters */}
            <div className="agent-k-filters">
              <button 
                className="agent-k-filter-chip"
                onClick={() => {
                  setQuery("3+ bedroom house under 500k with good schools");
                  setTimeout(() => inputRef.current?.form?.requestSubmit(), 100);
                }}
              >
                Family Starter
              </button>
              <button 
                className="agent-k-filter-chip"
                onClick={() => {
                  setQuery("Luxury homes over 1 million with pool");
                  setTimeout(() => inputRef.current?.form?.requestSubmit(), 100);
                }}
              >
                Luxury Estate
              </button>
              <button 
                className="agent-k-filter-chip"
                onClick={() => {
                  setQuery("New listings this week under 700k");
                  setTimeout(() => inputRef.current?.form?.requestSubmit(), 100);
                }}
              >
                Fresh Deals
              </button>
              <button 
                className="agent-k-filter-chip"
                onClick={() => {
                  setQuery("Condo or townhome with 2+ bedrooms");
                  setTimeout(() => inputRef.current?.form?.requestSubmit(), 100);
                }}
              >
                Low Maintenance
              </button>
            </div>

            {/* Active Filters */}
            {Object.keys(currentCriteria).length > 0 && (
              <div className="agent-k-active-filters">
                <h5 className="agent-k-active-filters-title">Active Filters</h5>
                <div className="agent-k-filter-tags">
                  {(currentCriteria.city || currentCriteria.location) && (
                    <span className="agent-k-filter-tag">
                      {currentCriteria.city || currentCriteria.location}
                      <button onClick={() => {
                        const newCriteria = { ...currentCriteria };
                        delete newCriteria.city;
                        delete newCriteria.location;
                        setCurrentCriteria(newCriteria);
                        onSearchCriteria(newCriteria);
                      }}>×</button>
                    </span>
                  )}
                  {(currentCriteria.bedrooms || currentCriteria.beds) && (
                    <span className="agent-k-filter-tag">
                      {currentCriteria.bedrooms || currentCriteria.beds}+ bed
                      <button onClick={() => {
                        const newCriteria = { ...currentCriteria };
                        delete newCriteria.bedrooms;
                        delete newCriteria.beds;
                        setCurrentCriteria(newCriteria);
                        onSearchCriteria(newCriteria);
                      }}>×</button>
                    </span>
                  )}
                  {(currentCriteria.maxPrice || currentCriteria.priceMax) && (
                    <span className="agent-k-filter-tag">
                      Under ${(currentCriteria.maxPrice || currentCriteria.priceMax).toLocaleString()}
                      <button onClick={() => {
                        const newCriteria = { ...currentCriteria };
                        delete newCriteria.maxPrice;
                        delete newCriteria.priceMax;
                        setCurrentCriteria(newCriteria);
                        onSearchCriteria(newCriteria);
                      }}>×</button>
                    </span>
                  )}
                  {(currentCriteria.minPrice || currentCriteria.priceMin) && (
                    <span className="agent-k-filter-tag">
                      Over ${(currentCriteria.minPrice || currentCriteria.priceMin).toLocaleString()}
                      <button onClick={() => {
                        const newCriteria = { ...currentCriteria };
                        delete newCriteria.minPrice;
                        delete newCriteria.priceMin;
                        setCurrentCriteria(newCriteria);
                        onSearchCriteria(newCriteria);
                      }}>×</button>
                    </span>
                  )}
                  {(currentCriteria.bathrooms || currentCriteria.baths) && (
                    <span className="agent-k-filter-tag">
                      {currentCriteria.bathrooms || currentCriteria.baths}+ bath
                      <button onClick={() => {
                        const newCriteria = { ...currentCriteria };
                        delete newCriteria.bathrooms;
                        delete newCriteria.baths;
                        setCurrentCriteria(newCriteria);
                        onSearchCriteria(newCriteria);
                      }}>×</button>
                    </span>
                  )}
                  {currentCriteria.pool && (
                    <span className="agent-k-filter-tag">
                      Pool
                      <button onClick={() => {
                        const newCriteria = { ...currentCriteria };
                        delete newCriteria.pool;
                        setCurrentCriteria(newCriteria);
                        onSearchCriteria(newCriteria);
                      }}>×</button>
                    </span>
                  )}
                  {currentCriteria.garage && (
                    <span className="agent-k-filter-tag">
                      Garage
                      <button onClick={() => {
                        const newCriteria = { ...currentCriteria };
                        delete newCriteria.garage;
                        setCurrentCriteria(newCriteria);
                        onSearchCriteria(newCriteria);
                      }}>×</button>
                    </span>
                  )}
                  {currentCriteria.propertyType && (
                    <span className="agent-k-filter-tag">
                      {currentCriteria.propertyType.replace('_', ' ')}
                      <button onClick={() => {
                        const newCriteria = { ...currentCriteria };
                        delete newCriteria.propertyType;
                        setCurrentCriteria(newCriteria);
                        onSearchCriteria(newCriteria);
                      }}>×</button>
                    </span>
                  )}
                  {currentCriteria.minSqft && (
                    <span className="agent-k-filter-tag">
                      {currentCriteria.minSqft.toLocaleString()}+ sqft
                      <button onClick={() => {
                        const newCriteria = { ...currentCriteria };
                        delete newCriteria.minSqft;
                        setCurrentCriteria(newCriteria);
                        onSearchCriteria(newCriteria);
                      }}>×</button>
                    </span>
                  )}
                  {currentCriteria.view && (
                    <span className="agent-k-filter-tag">
                      View
                      <button onClick={() => {
                        const newCriteria = { ...currentCriteria };
                        delete newCriteria.view;
                        setCurrentCriteria(newCriteria);
                        onSearchCriteria(newCriteria);
                      }}>×</button>
                    </span>
                  )}
                  {currentCriteria.waterfront && (
                    <span className="agent-k-filter-tag">
                      Waterfront
                      <button onClick={() => {
                        const newCriteria = { ...currentCriteria };
                        delete newCriteria.waterfront;
                        setCurrentCriteria(newCriteria);
                        onSearchCriteria(newCriteria);
                      }}>×</button>
                    </span>
                  )}
                  {(Object.keys(currentCriteria).length > 3) && (
                    <button 
                      className="agent-k-filter-tag"
                      style={{ background: '#ef4444' }}
                      onClick={() => {
                        setCurrentCriteria({});
                        onSearchCriteria({});
                        setAiResponse('All filters cleared. Ready for a fresh search!');
                      }}
                    >
                      Clear all
                    </button>
                  )}
                </div>
                
                {/* Save Search / Sign In buttons */}
                <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                  {isAuthenticated ? (
                    <button 
                      className="agent-k-btn primary"
                      onClick={() => {
                        if (Object.keys(currentCriteria).length > 0) {
                          setSearchName(`${currentCriteria.city || 'Properties'} - ${Object.keys(currentCriteria).length} filters`);
                          setShowSaveModal(true);
                        } else {
                          setAiResponse('Please add some search criteria before saving.');
                        }
                      }}
                      style={{ fontSize: '11px', padding: '4px 10px' }}
                    >
                      <FontAwesomeIcon icon={faSave} style={{ fontSize: '10px' }} />
                      Save Search
                    </button>
                  ) : (
                    <button 
                      className="agent-k-btn"
                      onClick={() => {
                        setShowSignInModal(true);
                      }}
                      style={{ fontSize: '11px', padding: '4px 10px' }}
                    >
                      <FontAwesomeIcon icon={faSignInAlt} style={{ fontSize: '10px' }} />
                      Sign In to Save
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Save Search Modal */}
      {showSaveModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '90%',
            maxWidth: '400px',
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#1e293b' }}>
              <FontAwesomeIcon icon={faSave} style={{ marginRight: '8px', color: '#3b82f6' }} />
              Save Your Search
            </h3>
            
            <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '16px' }}>
              Get notified when new properties match your criteria
            </p>
            
            <input
              type="text"
              placeholder="Name your search (e.g., 'Gilbert 3BR Pool')"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '14px',
                marginBottom: '16px'
              }}
              autoFocus
            />
            
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowSaveModal(false)}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  background: 'white',
                  color: '#64748b',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSearch}
                disabled={!searchName.trim() || saving}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  background: '#3b82f6',
                  color: 'white',
                  fontSize: '14px',
                  cursor: 'pointer',
                  opacity: (!searchName.trim() || saving) ? 0.5 : 1
                }}
              >
                {saving ? 'Saving...' : 'Save Search'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Sign In Modal */}
      {showSignInModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '90%',
            maxWidth: '400px',
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#1e293b' }}>
              <FontAwesomeIcon icon={faSignInAlt} style={{ marginRight: '8px', color: '#3b82f6' }} />
              Sign In to Your Account
            </h3>
            
            <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '16px' }}>
              Sign in to save searches and get property alerts
            </p>
            
            {signInError && (
              <div style={{
                padding: '10px',
                background: '#fee2e2',
                border: '1px solid #fecaca',
                borderRadius: '6px',
                color: '#dc2626',
                fontSize: '13px',
                marginBottom: '16px'
              }}>
                {signInError}
              </div>
            )}
            
            <form onSubmit={handleSignIn}>
              <input
                type="email"
                placeholder="Email address"
                value={signInData.email}
                onChange={(e) => setSignInData({ ...signInData, email: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '14px',
                  marginBottom: '12px'
                }}
                required
                autoFocus
              />
              
              <input
                type="password"
                placeholder="Password"
                value={signInData.password}
                onChange={(e) => setSignInData({ ...signInData, password: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '14px',
                  marginBottom: '16px'
                }}
                required
              />
              
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowSignInModal(false);
                    setSignInError('');
                    setSignInData({ email: '', password: '' });
                  }}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    background: 'white',
                    color: '#64748b',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={signInLoading || !signInData.email || !signInData.password}
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    borderRadius: '6px',
                    background: '#3b82f6',
                    color: 'white',
                    fontSize: '14px',
                    cursor: 'pointer',
                    opacity: (signInLoading || !signInData.email || !signInData.password) ? 0.5 : 1
                  }}
                >
                  {signInLoading ? 'Signing In...' : 'Sign In'}
                </button>
              </div>
            </form>
            
            <div style={{
              marginTop: '16px',
              paddingTop: '16px',
              borderTop: '1px solid #e2e8f0',
              fontSize: '13px',
              color: '#64748b',
              textAlign: 'center'
            }}>
              <p style={{ margin: '0 0 8px 0' }}>
                <a href="/forgot-password" style={{ color: '#3b82f6', textDecoration: 'none' }}>Forgot your password?</a>
              </p>
              <p style={{ margin: 0 }}>
                Don't have an account? <a href="/sign-up" style={{ color: '#3b82f6', textDecoration: 'none' }}>Sign up</a>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentK;